import React, { useEffect } from "react";
import clsx from "clsx";
import useEmblaCarousel from "embla-carousel-react";

import { ImageComponent } from "@app/core";
import { RelAttributes } from "@app/constants";
import { IOutFeaturedMerchantDTO } from "@app/core/new-merchant-card/featured-merchants.interface";
import { getOptimizedImageUrl } from "@app/util/image-optimization";
import { formatMessage } from "@app/translations/intl";

import { DotButton, useDotButton } from "./use-embla-dot-button";
import styles from "./merchant-slider.module.scss";

export const DEFAULT_MERCHANT_MAX = 30;

export type MerchantSliderProps = {
  title?: string;
  merchants?: IOutFeaturedMerchantDTO[];
  className?: string;
  startIndex?: number;
  loop?: boolean;
};

const MerchantSlider = ({ merchants, title, startIndex, className, loop }: MerchantSliderProps): JSX.Element => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: "auto",
    loop
  });

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);

  useEffect(() => {
    const slides = emblaApi?.scrollSnapList();

    if (!slides || slides.length === 0 || startIndex === undefined) return;

    emblaApi?.scrollTo(startIndex % slides.length, true);
  }, [emblaApi, startIndex]);

  return (
    <div className={clsx(styles.root, className)}>
      {title && <div className={styles.title}>{title}</div>}

      <div className={styles.viewport} ref={emblaRef}>
        <div className={styles.slider}>
          {(merchants || []).map((merchant, index) => (
            <a
              key={index}
              className={styles.merchant}
              href={getMerchantLink(merchant.slug)}
              rel={RelAttributes.SPONSORED}
              target="_blank"
            >
              <ImageComponent
                alt={merchant.logoImage?.alt || ""}
                src={getOptimizedImageUrl({ url: merchant.logoImage?.url, height: 120, quality: 100 })}
                title={merchant.logoImage?.title || ""}
                forceImageTag
                isBlocking
              />
            </a>
          ))}
        </div>
      </div>

      <div className={styles.dots}>
        {scrollSnaps.map((_, index) => (
          <DotButton
            key={index}
            onClick={() => onDotButtonClick(index)}
            className={clsx(styles.dot, selectedIndex === index && styles.activeDot)}
          />
        ))}
      </div>
    </div>
  );
};

export { MerchantSlider };

const getMerchantLink = (slug?: string) => {
  if (!slug) return "";

  const urlAddition = formatMessage({ id: "global.urlAddition" });

  return urlAddition ? `/out/merchant-slider/${urlAddition}/${slug}/` : `/out/merchant-slider/${slug}/`;
};
